import type { ErpModule } from '@/api/platform.api';
import { SETTINGS_PATH } from '@/config/settingsRegistry';
import type { ScreenActionGrants, ScreenPermissionModuleGroup } from '@/utils/screenPermissionMatrix';

export const MODULE_SETTINGS_PATH_PREFIX = '__module-settings__/';

export function moduleSettingsGrantPath(erpModule: ErpModule): string;
export function moduleSettingsGrantPath(erpModule: ErpModule, canonicalPath: string): string;
export function moduleSettingsGrantPath(
  erpModule: ErpModule,
  canonicalPath?: string,
): string {
  if (canonicalPath === undefined) {
    return `${MODULE_SETTINGS_PATH_PREFIX}${erpModule}`;
  }
  const canonical = canonicalPath.split('?')[0];
  if (canonical === SETTINGS_PATH || canonical === '') {
    return `${MODULE_SETTINGS_PATH_PREFIX}${erpModule}`;
  }
  return `${MODULE_SETTINGS_PATH_PREFIX}${erpModule}${canonical}`;
}

export function isModuleSettingsGrantPath(path: string): boolean {
  return path.startsWith(MODULE_SETTINGS_PATH_PREFIX);
}

export function parseModuleSettingsGrantPath(
  path: string,
): { erpModule: ErpModule; canonicalPath: string } | null {
  if (!isModuleSettingsGrantPath(path)) return null;
  const rest = path.slice(MODULE_SETTINGS_PATH_PREFIX.length);
  const slash = rest.indexOf('/');
  if (slash < 0) {
    return { erpModule: rest as ErpModule, canonicalPath: SETTINGS_PATH };
  }
  return {
    erpModule: rest.slice(0, slash) as ErpModule,
    canonicalPath: rest.slice(slash) || SETTINGS_PATH,
  };
}

export function isSettingsNavPath(path: string): boolean {
  const base = path.split('?')[0];
  return base === SETTINGS_PATH || base.startsWith(`${SETTINGS_PATH}/`);
}

/** Copy legacy `/settings` grants onto module-scoped virtual keys when loading stored roles. */
export function migrateLegacySettingsGrants(
  stored: ScreenActionGrants | undefined | null,
  matrix: ScreenPermissionModuleGroup[],
): ScreenActionGrants {
  if (!stored) return {};
  const migrated: ScreenActionGrants = { ...stored };
  const virtualPaths = new Set(matrix.flatMap((group) => group.screens.map((row) => row.path)));

  for (const [path, actions] of Object.entries(stored)) {
    if (isModuleSettingsGrantPath(path)) continue;
    if (path !== SETTINGS_PATH && !path.startsWith(`${SETTINGS_PATH}/`)) continue;
    for (const virtualPath of virtualPaths) {
      if (!virtualPath.startsWith(MODULE_SETTINGS_PATH_PREFIX)) continue;
      const parsed = parseModuleSettingsGrantPath(virtualPath);
      if (!parsed) continue;
      const canonical =
        path === SETTINGS_PATH ? SETTINGS_PATH : path;
      if (parsed.canonicalPath !== canonical) continue;
      if (migrated[virtualPath]) continue;
      migrated[virtualPath] = { ...actions };
    }
  }

  return migrated;
}

export function moduleSettingsViewGranted(
  canonicalPath: string,
  erpModule: ErpModule,
  grants: ScreenActionGrants | undefined | null,
): boolean {
  if (!grants) return false;
  const virtual = moduleSettingsGrantPath(erpModule, canonicalPath);
  if (grants[virtual]?.view === true) return true;
  const base = canonicalPath.split('?')[0];
  if (grants[base]?.view === true) return true;
  return false;
}

export function canViewModuleSettingsNav(
  navPath: string,
  activeModule: ErpModule,
  grants: ScreenActionGrants | undefined | null,
  screenGrantsConfigured: boolean,
): boolean | undefined {
  if (!screenGrantsConfigured) return undefined;
  if (!isSettingsNavPath(navPath)) return undefined;
  return moduleSettingsViewGranted(navPath.split('?')[0], activeModule, grants);
}
